import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickerSelectionState } from "@mui/x-date-pickers/internals/hooks/usePicker";
import { useCallback, useEffect, useState } from "react";

import { BottomSheetHeader } from "../components/BottomSheetHeader";
import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";

interface DatePickerInputBottomSheetProps {
  modalState: UseModalState;
  initialDate?: Date;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  shouldAutoConfirmOnSelect?: boolean;
  shouldHideCloseButton?: boolean;
  onConfirm: (date: Date) => void;
}

export function DatePickerInputBottomSheet(props: DatePickerInputBottomSheetProps) {
  const {
    modalState,
    initialDate,
    disableFuture,
    minDate,
    maxDate,
    shouldAutoConfirmOnSelect = false,
    shouldHideCloseButton = false,
    onConfirm,
  } = props;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setSelectedDate(initialDate);
    }
  }, [modalState.modalIsOpen, initialDate]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      disableSwipeArea
      footer={
        shouldAutoConfirmOnSelect ? undefined : (
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="contained"
              disabled={!isDefined(selectedDate)}
              onClick={() => {
                if (!selectedDate) {
                  return;
                }

                onConfirm(selectedDate);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        )
      }
      header={<BottomSheetHeader onClose={shouldHideCloseButton ? undefined : handleClose} />}
      modalState={modalState}
    >
      <DialogContent>
        <Stack width="100%" spacing={4} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
              value={selectedDate}
              onChange={(newDate: Date | undefined, selectionState?: PickerSelectionState) => {
                if (isDefined(newDate)) {
                  setSelectedDate(newDate);
                  if (shouldAutoConfirmOnSelect && selectionState === "finish") {
                    onConfirm(newDate);
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
